import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

import ChooseItem from "areas/order/choose-item"
import ContactInformation from "areas/order/contact-information"
import processOrder from "areas/order/helper/process-order"
import Reason from "areas/order/reason"

import {
  CancelButton,
  FormContainer,
  InputWrapper,
  OrderNumber,
  SubmitButton,
} from "../components"
import Heading from "../heading"

import validationSchema from "./validate"

const OrderForm = ({ orderData }) => {
  const navigate = useNavigate()
  const numEligibleItems = orderData.order.number_of_eligible_items
  const defaultItem =
    numEligibleItems === 1
      ? orderData.order.line_items.find(
          (item) => !item.error_message && !item.has_in_progress_rma
        )
      : null

  const {
    control,
    formState: { errors, isSubmitting, isValid, touchedFields },
    handleSubmit,
    setError,
    watch,
  } = useForm({
    defaultValues: {
      contact_email: "",
      contact_name: "",
      contact_phone_num: "",
      notes: "",
      returnItems: defaultItem ? [defaultItem.id] : [],
      rma_reason: "",
    },
    mode: "onBlur",
    resolver: yupResolver(validationSchema(orderData)),
  })

  const onSubmit = async (values) => {
    const formValues = {
      ...values,
      order_number: orderData.order.number,
      returnItems: values.returnItems,
    }

    if (values.rma_reason === "Ring resize") {
      navigate(
        `/order/resizing?order_number=${orderData.order.number}&name=${values.contact_name}&email=${values.contact_email}&phone=${values.contact_phone_num}`
      )
      return
    }

    try {
      await processOrder(formValues, "process_return")
      navigate("/order/completed_return")
    } catch (err) {
      console.log(err)
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <FormContainer>
      <Heading rmaType="return" />
      <Typography fontSize="18px" fontWeight="500" lineHeight="20px">
        Your Order Number:
      </Typography>
      <OrderNumber>{orderData.order.number}</OrderNumber>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ChooseItem
          control={control}
          errors={errors}
          formType="return"
          items={watch("returnItems")}
          lineItems={orderData.order.line_items}
          numEligibleItems={numEligibleItems}
        />

        {numEligibleItems > 0 && (
          <>
            <ContactInformation control={control} />
            <Reason
              control={control}
              formType="return"
              items={watch("returnItems")}
              line_items={orderData?.order?.line_items}
            />
          </>
        )}
        {numEligibleItems > 0 && (
          <InputWrapper>
            <SubmitButton
              disabled={!isValid || Object.keys(touchedFields).length === 0}
              loading={isSubmitting}
            >
              Process Request
            </SubmitButton>
          </InputWrapper>
        )}

        <InputWrapper>
          <CancelButton data-lc="return" to="/">
            Cancel and Continue Shopping
          </CancelButton>
        </InputWrapper>
        {errors?.root?.error && (
          <Typography color="error" mb={1}>
            {errors.root.error.message}
          </Typography>
        )}
      </form>
    </FormContainer>
  )
}

OrderForm.propTypes = {
  orderData: PropTypes.object,
}

export default OrderForm
